import { TransactionOperation } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useDisclosure } from 'further-ui/hooks';
import { CalendarDay } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { round } from 'lodash';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export type ReconcilableTransaction = {
  rowId: string;
  transactionId: string;
  transactionTypeId: string;
  operationType: TransactionOperation;
  isReceivable: boolean;
  isPayable: boolean;
  transactionDate: CalendarDay;
  description?: string;
  transactionCurrency: CurrencyCode;
  unreconciledAmountTcy: number;
  counterpartyLabel: string;
  isSelected: boolean;
};

export default function useReconcilableTransactions(vehicleId: string) {
  const disclosure = useDisclosure();

  const [initialReconcilableTransactions] =
    api.manualJournal.getReconciliableTransactions.useSuspenseQuery({
      vehicleId,
    });

  const [reconcilableTransactions, setReconcilableTransactions] = useState<
    Array<ReconcilableTransaction>
  >(() =>
    initialReconcilableTransactions.map((t) => ({
      ...t,
      transactionId: t.id,
      rowId: t.id,
      isSelected: false,
    })),
  );

  const [transactionToSplit, setTransactionToSplit] =
    useState<ReconcilableTransaction | null>(null);

  const setSelectedTransactions = (
    selectedTransactionRowIds: Array<string>,
  ) => {
    setReconcilableTransactions((prev) => {
      return prev.map((t) => ({
        ...t,
        isSelected: selectedTransactionRowIds.includes(t.rowId),
      }));
    });
  };

  const handleSelectTransactionToSplit = (transactionRowId: string) => {
    setTransactionToSplit(
      reconcilableTransactions.find((t) => t.rowId === transactionRowId) ??
        null,
    );

    disclosure.onOpen();
  };

  const splitSelectedTransaction = (amount: number) => {
    const currentSelectedTransactionRowIds = reconcilableTransactions
      .filter((t) => t.isSelected)
      .map((t) => t.rowId);

    if (!transactionToSplit) {
      return currentSelectedTransactionRowIds;
    }

    if (amount >= transactionToSplit.unreconciledAmountTcy) {
      return currentSelectedTransactionRowIds;
    }

    const splitIndex = reconcilableTransactions.findIndex(
      (transaction) =>
        transaction.transactionId === transactionToSplit.transactionId &&
        transaction.unreconciledAmountTcy >= amount,
    );

    if (splitIndex < 0) {
      return currentSelectedTransactionRowIds;
    }

    const sourceTransaction = reconcilableTransactions[splitIndex];
    const remainingAmount = round(
      sourceTransaction.unreconciledAmountTcy - amount,
      2,
    );

    const splitTransactions = [
      ...reconcilableTransactions.slice(0, splitIndex),
      ...[
        {
          ...sourceTransaction,
          unreconciledAmountTcy: amount,
          isSelected: true,
        },
        {
          ...sourceTransaction,
          rowId: uuidv4(),
          unreconciledAmountTcy: remainingAmount,
          isSelected: false,
        },
      ],
      ...reconcilableTransactions.slice(splitIndex + 1),
    ];

    setReconcilableTransactions(splitTransactions);

    setTransactionToSplit(null);
    disclosure.onClose();

    return splitTransactions.filter((t) => t.isSelected).map((t) => t.rowId);
  };

  return {
    reconcilableTransactions,
    setSelectedTransactions,
    showSplitTransactionDialog: disclosure.isOpen,
    closeSplitTransactionDialog: disclosure.onClose,
    transactionToSplit,
    selectTransactionToSplit: handleSelectTransactionToSplit,
    splitSelectedTransaction,
  };
}
